import {
  onBeforeUnmount,
  ref,
  watchEffect,
  type Ref,
} from 'vue';
import type { UseIntersectorOptions } from '@/types/index.mts';

/**
 * Custom hook that watches for intersection of an element or an array of elements
 * @param observable A ref to an element or an array of elements
 * @param options The intersection observer options
 * @returns An array of values indicating if the element is intersecting, 1 for true and 0 for false
 */
const useIntersector = (observable: Ref<HTMLElement[]>, options: UseIntersectorOptions) => {
  const observer = ref<IntersectionObserver | null>(null);
  const values = ref<number[]>([]);

  const initIntersectionObserver = () => {
    values.value = new Array(observable.value?.length || 1).fill(0);

    observer.value = new IntersectionObserver((entries) => {
      options?.onUpdate?.();

      entries.forEach((entry) => {
        if (entry.target instanceof HTMLElement) {
          const { isIntersecting } = entry;
          const index = Number(entry.target.dataset.index);

          values.value[index] = isIntersecting ? 1 : 0;

          if (isIntersecting) {
            options?.onIntersect?.({ entry, index });
          }
        }
      });
    }, options);

    if (!observable.value) {
      throw new Error('observable is required as a element or an array of elements');
    }

    if (Array.isArray(observable.value)) {
      observable.value.forEach((element) => {
        observer.value?.observe(element);
      });
    } else {
      observer.value.observe(observable.value);
    }
  };

  watchEffect(() => {
    if (observable.value?.length) {
      initIntersectionObserver();
    }
  });

  onBeforeUnmount(() => {
    observer.value?.disconnect();
  });

  return values;
};

export default useIntersector;
